import { ButtonLink, PrimaryButton } from '@designsforhealth/dfh-react-components'
import { useLocation } from '@gatsbyjs/reach-router'
import React, { FC } from 'react'
import styled from 'styled-components'

import useSiteMetadata from '../../../hooks/useSiteMetadata'
import { useAuth } from '../../../lib/auth'
import { usePageUrl } from '../../../lib/pages/hooks/usePageUrl'
import Spinner from '../../Spinner'
import CartDetailsContainer from './CartDetailsContainer'
import CartHeader from './CartHeader'
import CartModal from './CartModal'

import closeModalIcon from '../../../img/close_modal.svg'

export interface LoginToViewCartProps {
  className?: string
  toggleCartModal(): void
  isCartOpen: boolean
}

const LoginToViewCart: FC<LoginToViewCartProps> = ({ toggleCartModal, isCartOpen }) => {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth()
  const { featureFlags } = useSiteMetadata() ?? {}
  const location = useLocation()
  const { navigate } = usePageUrl()

  // Check if anonymous cart is enabled
  const anonymousCartEnabled = featureFlags?.anonymousCart ?? false

  return (
    <CartModal
      allowScroll
      isOpen={isCartOpen}
      onBackgroundClick={toggleCartModal}
      onEscapeKeydown={toggleCartModal}
    >
      <CartDetailsContainer>
        <CartHeader>
          <strong>YOUR CART:</strong>
          <ButtonLink onClick={toggleCartModal} underline={false}>
            <img alt="Close cart modal" src={closeModalIcon} />
          </ButtonLink>
        </CartHeader>
        <div className="px-6 py-8">
          {isLoading || isAuthenticated ? (
            <Spinner loading />
          ) : (
            <div className="text-center">
              <h3 className="text-xl font-semibold mb-4">
                {anonymousCartEnabled
                  ? 'Sign in to sync your cart across devices'
                  : 'Please log in to view your cart'}
              </h3>
              <p className="text-gray-600 mb-6">
                {anonymousCartEnabled
                  ? 'You can shop as a guest, but signing in will save your cart and provide a faster checkout experience.'
                  : 'An account is required to add items to your cart and make purchases.'}
              </p>
              <PrimaryButton
                onClick={() => {
                  loginWithRedirect({
                    // use relative path to avoid unloading application
                    appState: {
                      returnTo: location ? `${location.pathname}${location.search}` : '',
                    },
                  }).catch(() => navigate('/login'))
                }}
                text={anonymousCartEnabled ? 'Sign In' : 'Login to view cart'}
              />
              {anonymousCartEnabled && (
                <div className="mt-4">
                  <ButtonLink
                    onClick={toggleCartModal}
                    underline={true}
                    className="text-sm text-gray-500"
                  >
                    Continue shopping as guest
                  </ButtonLink>
                </div>
              )}
            </div>
          )}
        </div>
      </CartDetailsContainer>
    </CartModal>
  )
}

const StyledLoginToViewCart = styled(LoginToViewCart)``

export default StyledLoginToViewCart
